'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  FindingStatusChangeReasonDialog,
  isCustomerFindingStatus,
  shouldOpenStatusReasonDialog,
  toasterApi,
  type CustomerFindingStatus,
} from '@terra/ui-design-system';

type BeginIfRequiredParams = {
  currentStatus: string;
  nextStatus: string;
};

export type FindingStatusReasonSubmitPayload = {
  status: CustomerFindingStatus;
  note: string;
};

type UseFindingStatusReasonDialogParams = {
  isSubmitting?: boolean;
  onSubmit: (payload: FindingStatusReasonSubmitPayload) => Promise<boolean>;
  onDismiss?: () => void;
};

export const useFindingStatusReasonDialog = ({
  isSubmitting = false,
  onSubmit,
  onDismiss,
}: UseFindingStatusReasonDialogParams) => {
  const t = useTranslations('designSystem.findings.findingDrawer');
  const [pendingStatus, setPendingStatus] =
    useState<CustomerFindingStatus | null>(null);
  const [statusNote, setStatusNote] = useState('');

  const close = useCallback(() => {
    setPendingStatus(null);
    setStatusNote('');
    onDismiss?.();
  }, [onDismiss]);

  const beginIfRequired = useCallback(
    ({ currentStatus, nextStatus }: BeginIfRequiredParams): boolean => {
      if (
        !isCustomerFindingStatus(currentStatus) ||
        !isCustomerFindingStatus(nextStatus)
      ) {
        return false;
      }

      if (
        !shouldOpenStatusReasonDialog({
          currentStatus,
          nextStatus,
        })
      ) {
        return false;
      }

      setPendingStatus(nextStatus);
      setStatusNote('');

      return true;
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!pendingStatus) {
      return;
    }

    const trimmedNote = statusNote.trim();

    if (!trimmedNote) {
      return;
    }

    try {
      const didSave = await onSubmit({
        status: pendingStatus,
        note: trimmedNote,
      });

      if (didSave) {
        close();
      }
    } catch (error: unknown) {
      toasterApi.error({
        title: t('statusUpdate.errorTitle'),
        description:
          error instanceof Error && error.message
            ? error.message
            : t('statusUpdate.errorDescription'),
      });
    }
  }, [close, onSubmit, pendingStatus, statusNote, t]);

  const dialog = (
    <FindingStatusChangeReasonDialog
      open={pendingStatus !== null}
      onOpenChange={(open) => {
        if (!open) {
          close();
        }
      }}
      pendingStatus={pendingStatus}
      statusNote={statusNote}
      onStatusNoteChange={setStatusNote}
      onCancel={close}
      onSubmit={() => {
        void handleSubmit();
      }}
      isSubmitting={isSubmitting}
    />
  );

  return { beginIfRequired, dialog };
};
